'use client';

import { useState, useEffect, useCallback } from 'react';

interface Props {
  productMetadata?: Record<string, any>;
}

interface MeasurementPoint {
  id: string;
  x: number;
  y: number;
  label: string;
}

interface Measurement {
  from: MeasurementPoint;
  to: MeasurementPoint;
  distance: string;
}

export function MeasurementTool({ productMetadata }: Props) {
  const [active, setActive] = useState(false);
  const [points, setPoints] = useState<MeasurementPoint[]>([]);
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [unit, setUnit] = useState<'cm' | 'in' | 'mm'>('cm');

  const dims = (productMetadata as any)?.dimensions;
  const scale = dims?.scale || 1;

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'm' && !e.ctrlKey && !e.metaKey) {
        const tag = (e.target as HTMLElement).tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        setActive((v) => !v);
      }
      if (e.key === 'Escape' && active) {
        setPoints([]);
        setActive(false);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [active]);

  const handleCanvasClick = useCallback((e: MouseEvent) => {
    if (!active) return;
    const canvas = e.target as HTMLElement;
    if (canvas.tagName !== 'CANVAS') return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const id = `p-${Date.now()}`;
    const point: MeasurementPoint = { id, x, y, label: `P${points.length + 1}` };

    if (points.length === 1) {
      const from = points[0];
      const dx = (point.x - from.x) * scale;
      const dy = (point.y - from.y) * scale;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const formatted = formatDistance(dist, unit);

      setMeasurements((prev) => [...prev, { from, to: point, distance: formatted }]);
      setPoints([]);
    } else {
      setPoints([point]);
    }
  }, [active, points, scale, unit]);

  useEffect(() => {
    if (active) {
      document.addEventListener('click', handleCanvasClick);
    }
    return () => document.removeEventListener('click', handleCanvasClick);
  }, [active, handleCanvasClick]);

  function formatDistance(px: number, u: string): string {
    const cmPerPx = 0.1;
    const cm = px * cmPerPx;
    switch (u) {
      case 'mm': return `${(cm * 10).toFixed(0)} mm`;
      case 'in': return `${(cm / 2.54).toFixed(1)} in`;
      default: return `${cm.toFixed(1)} cm`;
    }
  }

  function clearMeasurements() {
    setMeasurements([]);
    setPoints([]);
  }

  return (
    <>
      <button
        onClick={() => setActive(!active)}
        className={`fixed bottom-4 left-4 z-50 p-2.5 rounded-full shadow-lg transition-all ${
          active
            ? 'bg-yellow-500 text-white ring-2 ring-yellow-300'
            : 'bg-white dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700'
        }`}
        title="Measurement tool (M)"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Z" />
          <path d="m14.5 12.5 2-2" />
          <path d="m11.5 9.5 2-2" />
          <path d="m8.5 6.5 2-2" />
          <path d="m17.5 15.5 2-2" />
        </svg>
      </button>

      {active && (
        <div className="fixed top-4 left-4 z-50 bg-white dark:bg-zinc-900 rounded-xl shadow-lg border border-zinc-200 dark:border-zinc-800 p-3 space-y-2 w-48">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold">Measure</span>
            <div className="flex gap-0.5">
              {(['cm', 'in', 'mm'] as const).map((u) => (
                <button
                  key={u}
                  onClick={() => setUnit(u)}
                  className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                    unit === u ? 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900' : 'text-zinc-400'
                  }`}
                >
                  {u}
                </button>
              ))}
            </div>
          </div>

          <p className="text-[10px] text-zinc-400">
            {points.length === 0 ? 'Click two points on the 3D model to measure' : 'Click a second point to complete'}
          </p>

          {measurements.length > 0 && (
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {measurements.map((m, i) => (
                <div key={i} className="flex items-center justify-between text-xs bg-zinc-50 dark:bg-zinc-800 rounded px-2 py-1">
                  <span className="text-zinc-500">{m.from.label} → {m.to.label}</span>
                  <span className="font-mono font-semibold">{m.distance}</span>
                </div>
              ))}
            </div>
          )}

          {measurements.length > 0 && (
            <button onClick={clearMeasurements} className="text-[10px] text-red-500 hover:text-red-600">
              Clear all
            </button>
          )}
        </div>
      )}

      {active && points.length === 1 && (
        <div
          className="fixed z-40 pointer-events-none"
          style={{
            left: points[0].x - 4,
            top: points[0].y - 4,
            width: 8,
            height: 8,
          }}
        >
          <div className="w-full h-full rounded-full bg-yellow-500 ring-2 ring-yellow-300 animate-pulse" />
        </div>
      )}

      {measurements.map((m, i) => {
        const midX = (m.from.x + m.to.x) / 2;
        const midY = (m.from.y + m.to.y) / 2;
        return (
          <div key={i} className="fixed z-40 pointer-events-none">
            <svg className="fixed inset-0 w-full h-full" style={{ zIndex: 39 }}>
              <line x1={m.from.x} y1={m.from.y} x2={m.to.x} y2={m.to.y} stroke="#eab308" strokeWidth="2" strokeDasharray="4 2" />
              <circle cx={m.from.x} cy={m.from.y} r="3" fill="#eab308" />
              <circle cx={m.to.x} cy={m.to.y} r="3" fill="#eab308" />
            </svg>
            <div
              className="fixed z-40 px-1.5 py-0.5 rounded bg-yellow-500 text-white text-[10px] font-mono font-bold pointer-events-none"
              style={{ left: midX - 20, top: midY - 10 }}
            >
              {m.distance}
            </div>
          </div>
        );
      })}
    </>
  );
}
